/* eslint-disable @typescript-eslint/no-explicit-any */
// https://docs.newrelic.com/docs/browser/new-relic-browser/browser-apis/log/
import { v4 as uuidv4 } from 'uuid';

import { store } from '../store';
import {
  ENABLE_NEW_RELIC_LOGGING,
  NEW_RELIC_LICENSE_KEY,
} from '../environment';

class NewRelicSingleton {
  private browserAgent: any | null = null;
  private sessionId: string;

  constructor() {
    this.sessionId = uuidv4();
  }

  async initializeBrowserAgent() {
    if (
      typeof window === 'undefined' ||
      this.browserAgent ||
      ENABLE_NEW_RELIC_LOGGING !== 'true'
    )
      return;

    try {
      const { BrowserAgent } = await import(
        '@newrelic/browser-agent/loaders/browser-agent'
      );
      const { Logging } = await import(
        '@newrelic/browser-agent/features/logging'
      );

      const options = {
        init: {
          distributed_tracing: { enabled: true },
          privacy: { cookies_enabled: true },
          ajax: { deny_list: ['bam.nr-data.net'] },
        },
        info: {
          beacon: 'bam.nr-data.net',
          errorBeacon: 'bam.nr-data.net',
          licenseKey: NEW_RELIC_LICENSE_KEY,
          applicationID: '1386210755',
          sa: 1,
        },
        loader_config: {
          accountID: '4793499',
          trustKey: '4793499',
          agentID: '1386210755',
          licenseKey: NEW_RELIC_LICENSE_KEY,
          applicationID: '1386210755',
        },
        features: [Logging],
      };

      this.browserAgent = new BrowserAgent(options);
      console.log('BrowserAgent Initialized!');
    } catch (error) {
      console.error('Failed to initialize New Relic:', error);
    }
  }

  async logCartAction(
    message: string,
    action: string,
    action_payload: object | number | string = '',
  ): Promise<void> {
    if (typeof window === 'undefined' || ENABLE_NEW_RELIC_LOGGING !== 'true')
      return;

    await this.initializeBrowserAgent();
    if (!this.browserAgent) return;

    try {
      const currentState = store.getState();
      const clonedPayload = deepClone(action_payload);
      const clonedSelections = deepClone(currentState.checkout.cart.selections);
      const clonedAddonSelections = deepClone(
        currentState.checkout.cart.addOnSelections,
      );
      const selections = JSON.stringify(cleanObject(clonedSelections));
      const addonSelections = JSON.stringify(
        cleanObject(clonedAddonSelections),
      );

      this.browserAgent.log(message, {
        level: 'DEBUG',
        customAttributes: {
          action_level: 'CART_ACTION',
          action,
          action_payload: clonedPayload,
          sessionId: this.sessionId,
          selections,
          addonSelections,
        },
      });
    } catch (error) {
      console.error('Failed to log cart action:', error);
    }
  }
}

// Helper function to deep clone an object
function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(deepClone) as any;
  }

  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, deepClone(value)]),
  ) as T;
}

function cleanObject(obj: any): any {
  if (Array.isArray(obj)) {
    const cleanedArray = obj
      .filter(
        (item) =>
          (item ?? '') !== '' &&
          !(Array.isArray(item) && item.length === 0) &&
          !(typeof item === 'object' && Object.keys(item).length === 0),
      )
      .map((item) => cleanObject(item));
    return cleanedArray.length > 0 ? cleanedArray : undefined;
  } else if (typeof obj === 'object' && obj !== null) {
    const cleanedEntries = Object.entries(obj)
      .map(([key, value]) => [key, cleanObject(value)])
      .filter(
        ([_, value]) =>
          (value ?? '') !== '' &&
          !(Array.isArray(value) && value.length === 0) &&
          !(typeof value === 'object' && Object.keys(value).length === 0),
      );
    return cleanedEntries.length > 0
      ? Object.fromEntries(cleanedEntries)
      : undefined;
  }
  return obj;
}

export const newRelic = new NewRelicSingleton();
